import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { CloudSync as SaveIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

interface Conditions {
  condiciones_generales_default: string;
  condiciones_adicionales_default: string;
}

// Condiciones generales/adicionales por defecto de un presupuesto nuevo.
// Antes vivía como una pestaña dentro de "Google Sheets" (nombre que no
// tenía nada que ver con esto); ahora es su propia página de menú.
export const ConditionsConfig: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [conditions, setConditions] = useState<Conditions>({
    condiciones_generales_default: '',
    condiciones_adicionales_default: ''
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConditions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.get<Conditions>('/app-settings/conditions');
      setConditions(data);
      setSaved(false);
    } catch (err) {
      console.error('Error loading conditions:', err);
      setError('Error al cargar las condiciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveConditions = async () => {
    try {
      setLoading(true);
      setError(null);
      await apiService.put('/app-settings/conditions', conditions);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving conditions:', err);
      setError('Error al guardar las condiciones');
    } finally {
      setLoading(false);
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Box p={3}>
        <Alert severity="error">
          No tenés permisos para acceder a esta sección. Solo los administradores pueden editar las condiciones.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" gutterBottom>
        Condiciones
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Condiciones generales y adicionales que se cargan por defecto en cada presupuesto nuevo.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {saved && (
            <Alert severity="success" sx={{ mb: 2 }}>
              ✓ Condiciones guardadas exitosamente
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={3}>
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Condiciones Generales
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={conditions.condiciones_generales_default}
                onChange={(e) => setConditions({
                  ...conditions,
                  condiciones_generales_default: e.target.value
                })}
                placeholder="Ingresa las condiciones generales de contratación..."
                disabled={loading}
                variant="outlined"
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Condiciones Adicionales
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={conditions.condiciones_adicionales_default}
                onChange={(e) => setConditions({
                  ...conditions,
                  condiciones_adicionales_default: e.target.value
                })}
                placeholder="Ingresa las condiciones adicionales..."
                disabled={loading}
                variant="outlined"
              />
            </Box>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={saveConditions}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Guardando...' : 'Guardar Condiciones'}
              </Button>
              <Button variant="outlined" onClick={loadConditions} disabled={loading}>
                Recargar
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="info">
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          💡 Cómo se usan estas condiciones:
        </Typography>
        <Typography variant="body2">
          Cuando un usuario crea un nuevo presupuesto, estos textos se cargan automáticamente en los campos correspondientes.
          El usuario puede modificarlos según sea necesario para ese presupuesto específico.
        </Typography>
      </Alert>
    </Box>
  );
};

export default ConditionsConfig;
